import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { EspecificacaoDoCarro, ItemDaNavegacao, NavegacaoDoCatalogo, PecaParaOCarro } from '@core/types';
import Aviso from './Aviso';
import Carregando from './Carregando';
import MiniaturaDaPeca from './MiniaturaDaPeca';
import OndeFica, { FaixaDoLugar, acaoPara, type AcaoNoOndeFica } from './OndeFica';
import { usePodeMudarEndereco } from '../lib/podeMudarEndereco';
import { fotosDasPecas, lugaresDasPecas, navegarCatalogo, pecasParaOCarro } from '../lib/consultas';
import { brl, qtd } from '../lib/formato';
import { lugaresParaMostrar, type LugaresDaPeca } from '../lib/ondeFica';
import {
  INICIO, agruparTipos, contagem, ondeServe, passo, separarPecas, trilha, type Comeco, type EstadoDaNavegacao,
} from '../lib/navegacaoDoCatalogo';
import { precoVigente } from '../lib/promocao';
import { mensagemDeErro } from '../lib/supabase';

// O catálogo por TIPO e por CARRO (pedido do dono, 01/10/2026 — ADR-009):
//   "Calotas → Fiat → Palio"   (comeco = 'tipo')
//   "Fiat → Palio → Óleo"      (comeco = 'carro'), e o óleo CERTO para o Palio.
//
// Cada passo é uma fileira de botões grandes — no balcão é dedo, não teclado —
// com quantas peças há atrás de cada um. Só aparece o que existe: nunca se
// navega para um beco vazio. As migalhas no topo voltam a qualquer passo.

const botao = 'border border-edge px-3 py-2.5 text-left hover:border-signal focus-visible:border-signal';

function Botoes({ itens, aoEscolher }: { itens: ItemDaNavegacao[]; aoEscolher: (i: ItemDaNavegacao) => void }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {itens.map((i) => (
        <button key={i.chave} type="button" className={botao} onClick={() => aoEscolher(i)}>
          <span className="block font-bold leading-tight">{i.nome}</span>
          <span className="mt-0.5 block text-xs text-dim">{contagem(i)}</span>
        </button>
      ))}
    </div>
  );
}

function Especificacoes({ lista, carro, tipo }: { lista: EspecificacaoDoCarro[]; carro: string; tipo: string }) {
  if (lista.length === 0) return null;
  return (
    <section className="cartao space-y-2 p-4" aria-label={`O que o ${carro} pede`}>
      <h3 className="rotulo">{tipo}: o que o {carro} pede</h3>
      <ul className="space-y-2">
        {lista.map((e) => (
          <li key={e.id} className="border-l-2 border-edge pl-3 text-sm">
            <p className="font-bold">
              {[e.geracao, e.anos, e.motor].filter(Boolean).join(' · ')}
            </p>
            <p className="text-mute">{e.valor}</p>
            <p className="mt-0.5 text-xs">
              {e.aprovado
                ? <span className="text-ok">conferido pelo dono</span>
                : <span className="text-alerta">a conferir — confirme no manual antes de vender</span>}
              {e.fonte && <span className="text-dim"> · fonte: {e.fonte}</span>}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function CartaoDaPeca({ p, fotos, aoEscolher, mostrarOnde = false, lugares, acaoNoOndeFica }: {
  p: PecaParaOCarro;
  fotos: Awaited<ReturnType<typeof fotosDasPecas>>;
  aoEscolher: (p: PecaParaOCarro) => void;
  /** Mostra as gerações/motores em que a peça serve (lista "em parte"). */
  mostrarOnde?: boolean;
  /** Os lugares da peça (lugaresDasPecas); sem eles, vale o local que a lista trouxe. */
  lugares?: LugaresDaPeca;
  acaoNoOndeFica?: AcaoNoOndeFica<PecaParaOCarro>;
}) {
  const preco = precoVigente(p);
  // 'mudar' abre a folha já no "Pôr num lugar" (dono e gerente, peça sem lugar).
  const [ondeAberto, setOndeAberto] = useState<false | 'ver' | 'mudar'>(false);
  const podeMudarEndereco = usePodeMudarEndereco();
  const principal = lugares ? lugares.principal : p.local;
  const lugaresDaPeca = lugaresParaMostrar(principal, lugares?.extras ?? []);
  return (
    <li className="cartao">
    <div className="flex items-start gap-3 p-3">
      {/* A foto é botão IRMÃO do cartão: botão dentro de botão é HTML inválido. */}
      <MiniaturaDaPeca productId={p.product_id} descricao={p.descricao} tamanho={56}
                       fotos={fotos.filter((f) => f.product_id === p.product_id)} />
      <button type="button" className="min-w-0 flex-1 text-left" onClick={() => aoEscolher(p)}>
        <p className="text-[0.9375rem] font-medium leading-snug">{p.descricao}</p>
        <p className="mt-0.5 text-xs text-dim">
          <span className="numero">{p.sku}</span>{p.marca ? ` · ${p.marca}` : ''}
        </p>
        {p.caracteristicas && <p className="text-xs text-mute">{p.caracteristicas}</p>}
        {mostrarOnde && p.servem_em && p.servem_em.length > 0 && (
          <p className="mt-1 text-xs text-alerta">
            Serve em: {p.servem_em.map(ondeServe).join('; ')}
            {p.servem_em.every((o) => !o.aprovado) && ' (a conferir)'}
          </p>
        )}
        {p.detalhes && <p className="mt-1 line-clamp-2 text-xs text-dim">{p.detalhes}</p>}
        {/* O estoque em selo (Grafite): verde tem; sem estoque é neutro. */}
        <span className={`mt-1.5 ${p.quantidade > 0 ? 'selo selo-pago' : 'selo'}`}>
          {p.quantidade > 0 ? `${qtd(p.quantidade)} em estoque` : 'sem estoque'}
        </span>
      </button>
      <span className="numero shrink-0 text-base text-paper">{preco ? brl(preco) : '—'}</span>
    </div>
      {/* O lugar era texto apagado no fim da linha; agora é a mesma faixa
          "Onde fica" da busca (06/10/2026), FORA do botão do cartão. */}
      <FaixaDoLugar lugares={lugaresDaPeca} area={lugares?.area ?? null} localBruto={principal} aoAbrir={() => setOndeAberto('ver')}
                    aoPorNumLugar={podeMudarEndereco ? () => setOndeAberto('mudar') : undefined} />
      {ondeAberto && (
        <OndeFica
          aoFechar={() => setOndeAberto(false)}
          comecarMudando={ondeAberto === 'mudar'}
          peca={{
            product_id: p.product_id, descricao: p.descricao, sku: p.sku, marca: p.marca,
            quantidade: p.quantidade, lugares: lugaresDaPeca, area: lugares?.area ?? null,
          }}
          acao={acaoPara(acaoNoOndeFica, p)}
        />
      )}
    </li>
  );
}

export default function NavegarCatalogo({ comeco, onEscolher, acaoNoOndeFica }: {
  comeco: Comeco;
  onEscolher: (p: PecaParaOCarro) => void;
  /** O botão da folha "Onde fica" (ver BuscaPecas). */
  acaoNoOndeFica?: AcaoNoOndeFica<PecaParaOCarro>;
}) {
  const [estado, setEstado] = useState<EstadoDaNavegacao>(() => INICIO(comeco));
  const [ano, setAno] = useState('');
  const agora = passo(estado);

  const nivel = useQuery({
    queryKey: ['catalogo-nav', agora.mostra === 'pecas' ? null : agora.consulta],
    queryFn: () => navegarCatalogo(agora.mostra === 'pecas' ? {} : agora.consulta),
    enabled: agora.mostra !== 'pecas',
    staleTime: 60_000,
  });

  const anoNum = /^\d{4}$/.test(ano) ? Number(ano) : null;
  const pecas = useQuery({
    queryKey: ['catalogo-pecas', agora.mostra === 'pecas' ? [agora.tipo, agora.modelo, anoNum] : null],
    queryFn: () => (agora.mostra === 'pecas'
      ? pecasParaOCarro({ tipo: agora.tipo, modelo: agora.modelo, ano: anoNum })
      : Promise.resolve(null)),
    enabled: agora.mostra === 'pecas',
  });

  const ids = useMemo(() => (pecas.data?.pecas ?? []).map((p) => p.product_id), [pecas.data]);
  const fotos = useQuery({
    queryKey: ['fotos-do-catalogo', ids],
    queryFn: () => fotosDasPecas(ids),
    enabled: ids.length > 0,
    staleTime: 60_000,
  });
  // Os lugares (principal e extras) da lista, num lote só, como as fotos.
  const lugares = useQuery({
    queryKey: ['lugares-das-pecas', ids],
    queryFn: () => lugaresDasPecas(ids),
    enabled: ids.length > 0,
    staleTime: 60_000,
  });
  const cartao = (p: PecaParaOCarro, mostrarOnde = false) => (
    <CartaoDaPeca key={p.product_id} p={p} fotos={fotos.data ?? []} aoEscolher={onEscolher}
                  mostrarOnde={mostrarOnde} lugares={lugares.data?.get(p.product_id)}
                  acaoNoOndeFica={acaoNoOndeFica} />
  );

  const migalhas = trilha(estado);
  const dados: NavegacaoDoCatalogo | undefined = nivel.data;

  const escolherTipo = (i: ItemDaNavegacao) => setEstado((e) => ({ ...e, tipo: { chave: i.chave, nome: i.nome, modo: i.modo } }));
  const escolherMontadora = (i: ItemDaNavegacao) => setEstado((e) => ({ ...e, montadora: { chave: i.chave, nome: i.nome } }));
  const escolherModelo = (i: ItemDaNavegacao) => setEstado((e) => ({ ...e, modelo: { chave: i.chave, nome: i.nome } }));

  const lista = (): ItemDaNavegacao[] => {
    if (!dados) return [];
    if (dados.nivel === 'inicio') return agora.mostra === 'tipos' ? dados.tipos : dados.montadoras;
    return dados.itens;
  };

  const titulo = {
    tipos: estado.modelo ? `O que procurar no ${estado.modelo.nome}?` : 'Que tipo de peça?',
    montadoras: estado.tipo ? `${estado.tipo.nome}: de que montadora?` : 'De que montadora é o carro?',
    modelos: `Qual ${estado.montadora?.nome ?? ''}?`,
    pecas: '',
  }[agora.mostra];

  const { servem, emParte, outras } = separarPecas(pecas.data?.pecas ?? [], estado.semCarro);
  const carro = estado.modelo?.nome ?? '';
  // "Em parte" com lugar dito (serve numa geração/motor e não em outra) x sem
  // chave para comparar (a especificação é texto: filtro, palheta, lanterna).
  const emParteComOnde = emParte.filter((p) => p.servem_em && p.servem_em.length > 0);
  const semComparar = emParte.filter((p) => !p.servem_em || p.servem_em.length === 0);

  return (
    <div className="space-y-4">
      <nav aria-label="Caminho" className="flex flex-wrap items-center gap-1.5 text-sm">
        <button type="button" className="border border-edge px-2.5 py-1 text-xs font-bold text-dim"
                onClick={() => { setEstado(INICIO(comeco)); setAno(''); }}>
          Início
        </button>
        {migalhas.map((m, i) => (
          <span key={`${m.rotulo}-${i}`} className="flex items-center gap-1.5">
            <span className="text-dim" aria-hidden="true">›</span>
            <button type="button" className="border border-edge px-2.5 py-1 text-xs font-bold"
                    aria-current={i === migalhas.length - 1 ? 'step' : undefined}
                    onClick={() => setEstado(m.volta)}>
              {m.rotulo}
            </button>
          </span>
        ))}
      </nav>

      {nivel.error ? <Aviso tipo="erro">{mensagemDeErro(nivel.error)}</Aviso> : null}
      {pecas.error ? <Aviso tipo="erro">{mensagemDeErro(pecas.error)}</Aviso> : null}

      {agora.mostra !== 'pecas' && (
        <section className="space-y-3">
          <h2 className="font-bold">{titulo}</h2>
          {nivel.isLoading && <Carregando texto="Carregando" />}
          {agora.mostra === 'tipos'
            ? agruparTipos(lista()).map((g) => (
                <div key={g.grupo} className="space-y-1.5">
                  <p className="rotulo">{g.grupo}</p>
                  <Botoes itens={g.tipos} aoEscolher={escolherTipo} />
                </div>
              ))
            : <Botoes itens={lista()} aoEscolher={agora.mostra === 'montadoras' ? escolherMontadora : escolherModelo} />}
          {agora.mostra === 'montadoras' && dados?.nivel === 'montadoras' && dados.modo === 'aplicacao'
            && dados.sem_carro > 0 && (
            <button type="button" className="botao-terciario"
                    onClick={() => setEstado((e) => ({ ...e, semCarro: true }))}>
              {dados.sem_carro === 1
                ? 'Ver a peça sem carro cadastrado (universal)'
                : `Ver as ${dados.sem_carro} sem carro cadastrado (universais)`}
            </button>
          )}
          {nivel.data && lista().length === 0 && (
            <p className="text-sm text-dim">
              Nada cadastrado aqui ainda. O tipo e o carro de cada peça se preenchem no cadastro dela.
            </p>
          )}
        </section>
      )}

      {agora.mostra === 'pecas' && (
        <section className="space-y-3">
          {agora.modelo && (
            <div className="flex items-end gap-2">
              <div>
                <label className="rotulo" htmlFor="nav-ano">Ano do carro (opcional)</label>
                <input id="nav-ano" className="campo w-28" inputMode="numeric" maxLength={4}
                       placeholder="ex.: 2012" value={ano} onChange={(e) => setAno(e.target.value.replace(/\D/g, ''))} />
              </div>
            </div>
          )}
          {pecas.isLoading && <Carregando texto="Procurando as peças" />}
          {pecas.data && (
            <>
              <Especificacoes lista={pecas.data.especificacoes} carro={carro} tipo={estado.tipo?.nome ?? ''} />
              {pecas.data.modo === 'especificacao' && agora.modelo && pecas.data.especificacoes.length === 0 && (
                anoNum ? (
                  <Aviso tipo="alerta">
                    Não há especificação do {carro} para {anoNum}.{' '}
                    <button type="button" className="font-bold underline" onClick={() => setAno('')}>
                      Ver de todos os anos
                    </button>
                  </Aviso>
                ) : (
                  <Aviso tipo="alerta">
                    Ainda não há especificação cadastrada para o {carro}. As peças abaixo são todas do tipo —
                    confira no manual qual serve.
                  </Aviso>
                )
              )}
              {servem.length > 0 && (
                <div className="space-y-2">
                  {pecas.data.modo === 'especificacao' && agora.modelo && (
                    <p className="rotulo">Servem no {carro}{anoNum ? ` ${anoNum}` : ''}</p>
                  )}
                  <ul className="space-y-2">
                    {servem.map((p) => cartao(p))}
                  </ul>
                </div>
              )}
              {emParteComOnde.length > 0 && (
                <div className="space-y-2">
                  <p className="rotulo">
                    Servem em parte do {carro}{anoNum ? ` ${anoNum}` : ''} — confira a geração e o motor
                    {!anoNum && ' (ou digite o ano)'}
                  </p>
                  <ul className="space-y-2">
                    {emParteComOnde.map((p) => cartao(p, true))}
                  </ul>
                </div>
              )}
              {semComparar.length > 0 && (
                <div className="space-y-2">
                  <p className="rotulo">Do mesmo tipo — confira no manual ou na aplicação qual serve no {carro}</p>
                  <ul className="space-y-2">
                    {semComparar.map((p) => cartao(p))}
                  </ul>
                </div>
              )}
              {outras.length > 0 && (
                <details className="space-y-2" open={servem.length === 0 && emParte.length === 0}>
                  <summary className="cursor-pointer text-sm text-mute">
                    {pecas.data.especificacoes.length > 0
                      ? `Outros do mesmo tipo (${outras.length}) — não batem com o que o ${carro} pede`
                      : `Outros do mesmo tipo (${outras.length})`}
                  </summary>
                  <ul className="mt-2 space-y-2">
                    {outras.map((p) => cartao(p))}
                  </ul>
                </details>
              )}
              {servem.length + emParte.length + outras.length === 0 && (
                <p className="cartao p-6 text-center text-sm text-mute">
                  {estado.semCarro
                    ? 'Nenhuma peça deste tipo sem carro cadastrado.'
                    : anoNum && pecas.data.modo === 'aplicacao'
                      ? <>Nenhuma peça para o {carro} {anoNum} neste tipo.{' '}
                          <button type="button" className="font-bold underline" onClick={() => setAno('')}>
                            Ver de todos os anos
                          </button></>
                      : `Nenhuma peça cadastrada ${carro ? `para o ${carro} ` : ''}neste tipo ainda.`}
                </p>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}
